import { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageToggle from "@/components/LanguageToggle";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2, ExternalLink, Check, CheckCircle2, LockKeyhole } from "lucide-react";

const PLANS = [
  {
    key: "solo",
    name: "Personal",
    price: "$10",
    features: ["3 números de WhatsApp", "1 subcuenta", "Menús y agente IA", "Sync al CRM"],
    link: "https://system.mktscaled.com/payment-link/6a83551fc8cc9a2ce7268654",
  },
  {
    key: "agencia",
    name: "Small Agency",
    price: "$25",
    features: ["15 números de WhatsApp", "3 subcuentas", "Menús y agente IA", "Sync al CRM"],
    link: "https://system.mktscaled.com/payment-link/6a835575c8cc9a2ce7268658",
    highlight: true,
  },
];

export default function Register() {
  const [searchParams] = useSearchParams();
  // The payment links redirect here with ?paid=1 after checkout: skip step 1.
  const cameFromPayment = searchParams.get("paid") === "1";
  const [unlocked, setUnlocked] = useState(cameFromPayment);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { registerPaid } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Completa todos los campos");
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("auth.toastPwdMismatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("auth.toastPwdShort"));
      return;
    }

    setLoading(true);
    try {
      await registerPaid(email, password, name);
      toast.success(t("auth.toastCreated"));
      navigate("/");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isPaymentRequired =
        error.response?.status === 402 || (detail && typeof detail === "object" && detail.error === "payment_required");
      const message =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        "No se pudo crear la cuenta";
      if (isPaymentRequired && cameFromPayment) {
        // Just paid: GHL may need a few seconds to expose the subscription.
        toast.error(
          "Estamos confirmando tu pago — espera unos 30 segundos y vuelve a intentar con el mismo correo.",
          { duration: 10000 }
        );
      } else {
        toast.error(message, { duration: 8000 });
        if (isPaymentRequired) {
          // Send them back to step 1 (the plans) so they can pay first.
          setUnlocked(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src="/isotipo.png" alt="LeadBot" className="w-10 h-10 rounded-xl" />
          <span className="text-2xl font-['Manrope'] font-extrabold text-foreground">
            LeadBot<span className="text-primary">Studio</span>
          </span>
          <LanguageToggle />
        </div>

        {/* Paso 1: elegir plan (se colapsa cuando ya vienen del pago) */}
        {cameFromPayment ? (
          <Card className="bg-white border-primary border-2 mb-8">
            <CardContent className="py-5 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-primary shrink-0" />
              <div>
                <p className="font-['Manrope'] font-bold">{t("auth.paidBanner")}</p>
                <p className="text-sm text-muted-foreground">{t("auth.paidBannerSub")}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 text-center">
              <h2 className="text-lg font-['Manrope'] font-bold">{t("auth.step1Title")}</h2>
              <p className="text-sm text-muted-foreground">{t("auth.step1Sub")}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {PLANS.map((plan) => (
                <Card
                  key={plan.key}
                  className={`bg-white ${plan.highlight ? "border-primary border-2" : "border-gray-200"}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-baseline justify-between font-['Manrope']">
                      <span>{plan.name}</span>
                      <span className="text-2xl font-extrabold text-primary">
                        {plan.price}
                        <span className="text-sm font-normal text-muted-foreground"> {t("auth.perMonth")}</span>
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <ul className="space-y-1 mb-4">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-primary shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button asChild variant={plan.highlight ? "default" : "outline"} className="w-full">
                      <a href={plan.link} target="_blank" rel="noopener noreferrer">
                        {t("auth.tryFree")}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Paso 2: crear cuenta (bloqueado hasta comprar o confirmar compra) */}
        <Card className="bg-white border-gray-200 relative">
          {!unlocked && (
            <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-[2px] rounded-lg flex flex-col items-center justify-center gap-3 p-6 text-center">
              <LockKeyhole className="w-8 h-8 text-muted-foreground" />
              <p className="font-['Manrope'] font-bold">{t("auth.firstPlan")}</p>
              <p className="text-sm text-muted-foreground max-w-xs">{t("auth.firstPlanSub")}</p>
              <Button variant="outline" onClick={() => setUnlocked(true)}>
                {t("auth.alreadyPaid")}
              </Button>
            </div>
          )}
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-['Manrope']">
              {cameFromPayment ? t("auth.step2Title") : t("auth.step2Title")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{t("auth.step2Sub")}</p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("auth.fullName")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder={t("auth.namePh")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.emailPaid")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
                    data-testid="register-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando tu pago...
                  </>
                ) : (
                  "Crear cuenta"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              {t("auth.haveAccount")}{" "}
              <Link to="/login" className="text-primary hover:underline">
                {t("auth.signIn")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
